import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../../../hooks';
import { safePostMessage, isEmbeddedInParent, isAllowedParentOrigin } from '../../../../utils';
import { createChatMessageFromUI } from '../../../../utils/chat-message';
import { runChatbotOpenRequests } from '../../../../utils/chatbotOpenRequests';
import { SenderType } from '../../../../types/chat-message';
import { PostMessageType } from '@constants/postMessage';
import type {
  BlockContent,
  CommandVariable,
  MessageButton,
  TextBlockPayload,
} from '../../../../types/chat-message';
import type { ChatMessage } from '../../../../types';
import {
  Conversation,
  AgentSwitchNotification,
  InputMessage,
  ChatHeader,
  ChatSideSheet,
  PdfListOverlay,
  CustomTooltip,
  GlobalToast,
  VoicePermissionModal,
} from '@pages/chatbot/components';
import CskhMessageToast from '../GlobalToast/CskhMessageToast';
import AudioPlayerBar from '../Conversation/AudioPlayerBar';
import MicPermissionDialog from '../MicPermissionDialog/MicPermissionDialog';
import {
  ChatAgentType,
  AGENT_BY_FEATURE_TAG,
  FEATURE_TAG_BY_AGENT,
  isAgentFeatureTag,
  SOCKET_TAG_BY_FEATURE_TAG,
} from '@constants/chat-agents';
import type { AiDisplaySetting } from '@services/chatService';
import { offset, shift } from '@floating-ui/dom';
import './EmbedBox.scss';

const createCskhTooltipMiddlewares = () => [
  offset(8),
  shift({ crossAxis: true, padding: 8 }),
];
const isHiddenButton = (commandVariables?: CommandVariable[]): boolean =>
  commandVariables?.some((v) => v.name === 'hidden_message' && v.value === 'true') ?? false;

const collectMessageButtons = (msg: ChatMessage): MessageButton[] => {
  const fromBlocks =
    msg.blockContents?.flatMap((block) => {
      if (block.type !== 'TEXT') return [];
      return (block.payload as TextBlockPayload).buttons || [];
    }) ?? [];
  return [...(msg.buttons || []), ...fromBlocks];
};

const findLiveCancelOrderButton = (log: ChatMessage[]): MessageButton | undefined => {
  for (let i = log.length - 1; i >= 0; i--) {
    const msg = log[i];
    if (msg.senderType !== SenderType.BOT || msg._isHistory) continue;
    const hasLiveOrder = msg.blockContents?.some((b) => b.type === 'ORDER_INFO');
    if (!hasLiveOrder) continue;
    return collectMessageButtons(msg).find((btn) => btn.intent === 'cancel_order');
  }
  return undefined;
};

const EmbedBox: React.FC = () => {
  const { t } = useTranslation();
  const {
    activeAgent,
    handleAgentChange,
    currentChatLog,
    currentHasChatHistory,
    connected,
    inputValue,
    listening,
    onChangeInput,
    handleMicrophoneClick,
    stopMicAndDiscard,
    pauseMicAndKeepText,
    cancelMicAndClearText,
    voiceErrorModalType,
    closeVoiceErrorModal,
    retryVoicePermission,
    handleChatSubmit,
    handleCommandClick,
    handleSwitchAgent,
    handleSkipSwitch,
    switchNotification,
    chatRef,
    agentConfig,
    onReadyConversation,
    isSTTSupported,
    micDialog,
    closeMicDialog,
    confirmMicDialog,
    isThinking,
    setIsThinking,
    commands,
    loadMoreHistory,
    loadNewerHistory,
    isFetchingMore,
    handleForwardToAgent,
    redirectData,
    closeRedirectOverlay,
    // Audio
    isAudioEnabled,
    toggleAudio,
    setAudioEnabled,
    isCommandsLoading,
    // Quick Selection
    quickSelectionData,
    handleInstructionClick,
    handleQuickFunctionClick,
    closeQuickSelection,
    handleQuickSelectionSelect,
    quickFunctions,
    quickFunctionSearchList,
    instructions,
    isInstructionsLoading,
    refreshInstructions,
    handleRateMessage,
    handleRetryUpload,
    quotedMessage,
    stopBotSpeaking,
    handleQuoteMessage,
    handleCancelQuote,
    scrollToQuotedMessage,
    markAllAsHistory,
    isChatInputDisabled,
    isBotSpeaking,
    isAudioPaused,
    isAudioPlayerVisible,
    audioElapsedSeconds,
    audioPlaybackRate,
    playBotAudio,
    toggleBotAudioPause,
    cycleBotAudioRate,
    hasPendingAudio,
    connectionStatuses,
    needsManualReconnect,
    manualReconnect,
    hasCSKHNotification,
    toastMessage,
    setToastMessage,
    chatSessionVersion,
    addMessage,
    updateMessage,
    getCurrentChatLog,
    user,
    handleStopStreaming,
    clearStoppedMessages,
    sendChangeModeMessage,
    refreshConversationId,
    startNewChatSession,
    loadCurrentChatSession,
    selectChatSession,
  } = useChat();

  useEffect(() => {
    console.log('[AudioPlayer] isAudioPlayerVisible =', isAudioPlayerVisible, {
      isAudioPaused,
      audioElapsedSeconds,
      audioPlaybackRate,
    });
  }, [isAudioPlayerVisible, isAudioPaused, audioElapsedSeconds, audioPlaybackRate]);

  // Track countdown active state from OrderCard via window event
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  /** Bump mỗi lần mở chatbot → refetch quick-suggestions (quyền có thể đổi) */
  const [quickSuggestionsRefreshKey, setQuickSuggestionsRefreshKey] = useState(0);
  /** Kết quả personalization từ runChatbotOpenRequests */
  const [aiDisplayFromOpen, setAiDisplayFromOpen] = useState<AiDisplaySetting | null>(null);
  const [aiDisplayOpenVersion, setAiDisplayOpenVersion] = useState(0);
  /** Bump mỗi lần đóng chatbot → thu gọn welcome / quick-suggestions */
  const [welcomeResetKey, setWelcomeResetKey] = useState(0);
  const [selectedPlusFeatureKey, setSelectedPlusFeatureKey] = useState<string | null>(null);
  const pendingAgentRef = useRef<ChatAgentType | null>(null);
  const sideSheetContainerRef = useRef<HTMLDivElement | null>(null);
  const activeAgentRef = useRef(activeAgent);
  activeAgentRef.current = activeAgent;
  const cskhTimeTooltipMiddlewares = useMemo(() => createCskhTooltipMiddlewares(), []);

  // Footer (ô input) phủ lên vùng chat và cao lên khi gõ nhiều dòng / trích dẫn / gắn tag →
  // xuất chiều cao thật ra CSS var để nút cuộn xuống & spacer cuối chat không bị ô input đè
  const footerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const footer = footerRef.current;
    const layout = footer?.parentElement;
    if (!footer || !layout || typeof ResizeObserver === 'undefined') return;
    const syncFooterHeight = () => {
      layout.style.setProperty('--chat-footer-height', `${footer.offsetHeight}px`);
    };
    syncFooterHeight();
    const observer = new ResizeObserver(syncFooterHeight);
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  const handleChatbotOpen = useCallback(() => {
    void runChatbotOpenRequests({
      activeAgent: activeAgentRef.current,
      loadCurrentChatSession,
      refreshWelcomeSuggestions: () => {
        setQuickSuggestionsRefreshKey((prev) => prev + 1);
      },
    }).then(({ aiDisplay }) => {
      if (aiDisplay) {
        setAiDisplayFromOpen(aiDisplay);
        setAiDisplayOpenVersion((prev) => prev + 1);
      }
    });
  }, [loadCurrentChatSession]);

  /**
   * Đóng chatbot (nút X / CHAT_TOGGLE close) → đưa UI về default.
   * Thêm reset state mới vào đây (không persist sang lần mở sau).
   * Hiện tại:
   * - đóng side sheet
   * - thu gọn quick-suggestions (welcomeResetKey)
   * - bật lại âm thanh
   */
  const resetUiOnClose = () => {
    setIsSideSheetOpen(false);
    setWelcomeResetKey((prev) => prev + 1);
    setSelectedPlusFeatureKey(null);
    setAudioEnabled(true);
  };

  useEffect(() => {
    const handler = (e: Event) => {
      setIsCountdownActive((e as CustomEvent).detail?.isActive ?? false);
    };
    window.addEventListener('order-countdown-change', handler);
    return () => window.removeEventListener('order-countdown-change', handler);
  }, []);

  // Voice: dialog micro nổi ở web cha để không bị cắt trong iframe 404px
  useEffect(() => {
    if (!micDialog || !isEmbeddedInParent()) return;
    safePostMessage({
      type: PostMessageType.CHATBOT_MIC_DIALOG_REQUEST,
      payload: { dialogType: micDialog },
    });
  }, [micDialog]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (!isAllowedParentOrigin(event.origin)) return;
      if (event.data?.type !== PostMessageType.CHATBOT_MIC_DIALOG_RESULT) return;
      if (event.data?.payload?.confirmed) {
        confirmMicDialog();
      } else {
        closeMicDialog();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [confirmMicDialog, closeMicDialog]);

  // Track typewriter stream animation active state
  // Lưu kèm messageId để chỉ tính animation của đúng tin cuối (tránh sự kiện cũ giữ nút "Ngưng")
  const [typewriterState, setTypewriterState] = useState<{ isAnimating: boolean; messageId?: string }>({
    isAnimating: false,
  });

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setTypewriterState({
        isAnimating: detail?.isAnimating ?? false,
        messageId: detail?.messageId != null ? String(detail.messageId) : undefined,
      });
    };
    window.addEventListener('typewriter-animation-change', handler);
    return () => window.removeEventListener('typewriter-animation-change', handler);
  }, []);

  useEffect(() => {
    setTypewriterState({ isAnimating: false });
  }, [activeAgent]);


  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === PostMessageType.CHAT_TOGGLE) {
        if (event.data.isOpen === false) {
          console.log('[EmbedBox] Received CHAT_TOGGLE (close) from parent, stopping voice and mic');
          resetUiOnClose();
          stopBotSpeaking();
          stopMicAndDiscard();
          handleStopStreaming();
          clearStoppedMessages();
          // Re-open always lands on Hỏi đáp (AI)
          pendingAgentRef.current = null;
          if (activeAgent !== ChatAgentType.AI) {
            handleAgentChange(ChatAgentType.AI);
          }
        } else if (event.data.isOpen === true) {
          console.log('[EmbedBox] Received CHAT_TOGGLE (open)');
          handleChatbotOpen();
        }
      }
    };
    window.addEventListener('message', handleWindowMessage);
    return () => window.removeEventListener('message', handleWindowMessage);
  }, [
    stopBotSpeaking,
    stopMicAndDiscard,
    handleStopStreaming,
    clearStoppedMessages,
    activeAgent,
    handleAgentChange,
    handleChatbotOpen,
  ]);

  // Mở lại chatbot (đã từng ẩn) → bootstrap lại requests
  useEffect(() => {
    const rootEl = document.querySelector('.embed-box');
    if (!rootEl) return;

    let hasBeenHidden = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          hasBeenHidden = true;
          return;
        }
        if (!hasBeenHidden) return;

        hasBeenHidden = false;
        console.log('[EmbedBox] Chatbot visible again');
        window.dispatchEvent(new CustomEvent('chatbot:opened'));
        handleChatbotOpen();
      });
    }, { threshold: 0.1 });

    observer.observe(rootEl);
    return () => {
      observer.disconnect();
    };
  }, [handleChatbotOpen]);

  // Track bot streaming state (thinking, active socket stream, or typewriter animating)
  const chatLog = getCurrentChatLog(activeAgent);
  const isWelcomeOnboarding =
    currentChatLog.length === 0 && currentHasChatHistory === false;
  const lastMsg = chatLog.length > 0 ? chatLog[chatLog.length - 1] : null;
  const isLastBotMessage = lastMsg?.senderType === SenderType.BOT && !lastMsg?._isHistory && !lastMsg?.isStopped;
  const isTypewriterAnimating =
    typewriterState.isAnimating &&
    isLastBotMessage &&
    (!typewriterState.messageId ||
      typewriterState.messageId === String(lastMsg?.id) ||
      typewriterState.messageId === String(lastMsg?.messageId));
  const isBotStreaming = isThinking ||
                         (isLastBotMessage && lastMsg?.isFinal === false) ||
                         isTypewriterAnimating;

  // BE đôi khi không gửi typeChangeFlow trên tin confirm → fallback text / nút
  const isConfirmSwitchPending = chatLog.some((m, index) => {
    if (m._isHistory || m.isConfirmDisabled) return false;
    if (m.confirmSwitchAgent) return true;
    if (index !== chatLog.length - 1) return false;
    if (m.typeChangeFlow === 'CHANGE_MODE_PENDING') return true;
    const text = `${m.text || ''} ${m.suggestAgentNotify || ''}`.toLowerCase();
    if (text.includes('chưa hoàn thành yêu cầu')) return true;
    return collectMessageButtons(m).some((b) => b.intent === 'change_flow_confirm');
  });
  // Đang countdown vẫn cho dùng nút "+"; chỉ khóa khi đang chờ confirm chuyển agent
  const isComponentPickerDisabled = isConfirmSwitchPending;

  // Countdown / đang confirm → gửi CHANGE_MODE (mỗi lần đổi agent → tin confirm mới)
  // Trả về false khi việc chuyển bị hoãn để ChatHeader không đổi sang agent mới ngay
  const handleAgentChangeGuarded = (agent: typeof activeAgent): boolean => {
    if (isCountdownActive || isConfirmSwitchPending) {
      pendingAgentRef.current = agent;
      sendChangeModeMessage(agent);
      return false;
    }
    handleAgentChange(agent);
    return true;
  };

  // Agent gắn theo tag ở ô input (thay thanh tab agent): chọn tag Thuế/Báo cáo/Hướng dẫn → đổi
  // sang agent tương ứng; gỡ tag hoặc chọn tag thường (Hình ảnh, Video...) → về Hỏi đáp (AI).
  const handlePlusFeatureChange = (key: string | null) => {
    if (key === 'manual') {
      void refreshInstructions();
    }
    const targetAgent = (key && AGENT_BY_FEATURE_TAG[key]) || ChatAgentType.AI;
    if (targetAgent !== activeAgent && activeAgent !== ChatAgentType.CSKH) {
      if (isThinking) return;
      // Tag agent được set lại bởi effect đồng bộ bên dưới khi agent đổi thật
      // (có thể bị hoãn nếu đang countdown / chờ confirm chuyển agent).
      if (!isAgentFeatureTag(key)) setSelectedPlusFeatureKey(key);
      handleAgentChangeGuarded(targetAgent);
      return;
    }
    setSelectedPlusFeatureKey(key);
  };

  // Đồng bộ tag theo agent khi agent đổi từ nơi khác (bot forward, confirm chuyển agent, đóng chatbot...)
  useEffect(() => {
    const agentTag = FEATURE_TAG_BY_AGENT[activeAgent];
    setSelectedPlusFeatureKey((prev) => {
      if (agentTag) return agentTag;
      return isAgentFeatureTag(prev) ? null : prev;
    });
  }, [activeAgent]);

  // Handle "Có" - add a fresh OrderCard message with restarted countdown
  const handleConfirmStay = (messageId: string, blockContents?: BlockContent[]) => {
    updateMessage(activeAgent, messageId, { isConfirmDisabled: true });
    pendingAgentRef.current = null;
    if (blockContents && blockContents.length > 0) {
      addMessage(activeAgent, createChatMessageFromUI({
        text: '',
        user: SenderType.BOT,
        isRead: true,
        agentType: activeAgent,
        time: new Date().toISOString(),
        blockContents,
      }));
    }
  };

  // Handle "Không" - switch to pending agent
  const handleConfirmLeave = async (messageId: string, pendingAgent: typeof activeAgent) => {
    updateMessage(activeAgent, messageId, { isConfirmDisabled: true });
    // Đợi tin nhắn "Không" gửi xong qua socket của agent hiện tại trước khi chuyển agent,
    // tránh race condition khiến tin nhắn bị gửi nhầm sang socket của targetAgent
    // (handleAgentChange đóng socket cũ ngay khi được gọi).
    await handleChatSubmit("Không");
    const targetAgent = pendingAgentRef.current || pendingAgent;
    if (targetAgent && targetAgent !== activeAgent) {
      handleAgentChange(targetAgent);
    }
    pendingAgentRef.current = null;
  };

  const handleClose = () => {
    resetUiOnClose();
    onChangeInput('');
    markAllAsHistory();
    stopMicAndDiscard();
    stopBotSpeaking();
    handleStopStreaming();
    clearStoppedMessages();
    pendingAgentRef.current = null;
    // Next open defaults to Hỏi đáp (AI)
    if (activeAgent !== ChatAgentType.AI) {
      handleAgentChange(ChatAgentType.AI);
    }
    // Huỷ countdown trong OrderCard khi đóng chatbot
    window.dispatchEvent(new CustomEvent('chatbot-close'));
    safePostMessage({
      type: PostMessageType.CHAT_TOGGLE,
      isOpen: false,
    });
  };

  // Before opening new chat, cancel live order if any
  const handleStartNewChat = async () => {
    setSelectedPlusFeatureKey(null);
    await refreshConversationId();
    await startNewChatSession();
  };

  const handleClickSetting = () => {
    if (!user?.permissions?.store_settings_all_view) {
      setToastMessage({
        text: t('common.noPermissions'),
        type: 'error',
      });
      return;
    }

    const targetRoute = '/setting/tendoo-ai';
    safePostMessage({
      type: PostMessageType.NAVIGATE_TO,
      path: targetRoute
    });
  };

  return (
    <div className={`embed-box${isWelcomeOnboarding ? '' : ' embed-box--chatting'}`}>
      <MicPermissionDialog
        type={isEmbeddedInParent() ? null : micDialog}
        onClose={closeMicDialog}
        onConfirm={confirmMicDialog}
      />

      {toastMessage?.type === 'cskh' ? (
        <CskhMessageToast
          message={toastMessage}
          onClick={() => {
            if (activeAgent !== ChatAgentType.CSKH) {
              handleAgentChangeGuarded(ChatAgentType.CSKH);
            }
            setToastMessage(null);
          }}
        />
      ) : (
        toastMessage && (
          <GlobalToast
            message={toastMessage}
            onClose={() => setToastMessage(null)}
          />
        )
      )}

      {isAudioPlayerVisible && (
        <AudioPlayerBar
          isPaused={isAudioPaused}
          elapsedSeconds={audioElapsedSeconds}
          playbackRate={audioPlaybackRate}
          onTogglePause={toggleBotAudioPause}
          onCycleRate={cycleBotAudioRate}
          onClose={stopBotSpeaking}
        />
      )}

      <div
        className={`pointer-events-none absolute inset-0 z-0 overflow-hidden bg-[linear-gradient(180deg,#F8F8F9_0%,#FFF5F0_80.93%,#FFD3BF_100%)] transition-opacity duration-500 ease-in-out ${
          isWelcomeOnboarding ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden={!isWelcomeOnboarding}
      />

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }}>
      <div
        ref={sideSheetContainerRef}
        style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 30, pointerEvents: isSideSheetOpen ? 'auto' : 'none' }}
      />
      <ChatHeader
        onClose={handleClose}
        onMenuClick={() => setIsSideSheetOpen(true)}
        showNewChat={activeAgent === ChatAgentType.AI}
        onNewChatClick={() => {
          void handleStartNewChat();
        }}
        isAudioEnabled={isAudioEnabled}
        onToggleAudio={toggleAudio}
        isBotSpeaking={isBotSpeaking}
        onClickSetting={handleClickSetting}
        activeAgent={activeAgent}
        onAgentChange={handleAgentChangeGuarded}
      />

      <ChatSideSheet
        open={isSideSheetOpen}
        onClose={() => setIsSideSheetOpen(false)}
        getContainer={sideSheetContainerRef.current ?? undefined}
        isAudioEnabled={isAudioEnabled}
        onToggleAudio={toggleAudio}
        isBotSpeaking={isBotSpeaking}
        typeChat={activeAgent}
        sessionsRefreshKey={chatSessionVersion}
        aiDisplayFromOpen={aiDisplayFromOpen}
        aiDisplayOpenVersion={aiDisplayOpenVersion}
        onNewChat={() => {
          void handleStartNewChat();
        }}
        onSelectConversation={(conversation) => {
          void selectChatSession(conversation.id, ChatAgentType.AI);
        }}
      />

      <div className="embed-box-layout">
        <div className="embed-box-upper">
          <div className="embed-box-main">
            <div className="embed-box-chat-section">
              <Conversation
                connected={connected}
                chatLog={currentChatLog}
                chatSessionVersion={chatSessionVersion}
                quickSuggestionsRefreshKey={quickSuggestionsRefreshKey}
                welcomeResetKey={welcomeResetKey}
                handleChatSubmit={handleChatSubmit}
                onReadyConversation={onReadyConversation}
                chatRef={chatRef as React.RefObject<HTMLDivElement | null>}
                hasChatHistory={currentHasChatHistory}
                activeAgent={activeAgent}
                isThinking={isThinking}
                setIsThinking={setIsThinking}
                isBotResponding={isBotStreaming}
                onScrollToBottom={() => chatRef.current?.scrollIntoView({ behavior: 'smooth' })}
                loadMoreHistory={loadMoreHistory}
                loadNewerHistory={loadNewerHistory}
                isFetchingMore={isFetchingMore}
                handleAgentChange={handleAgentChange}
                handleForwardToAgent={handleForwardToAgent}
                handleRateMessage={handleRateMessage}
                handleRetryUpload={handleRetryUpload}
                handleQuoteMessage={handleQuoteMessage}
                scrollToQuotedMessage={scrollToQuotedMessage}
                needsManualReconnect={needsManualReconnect[activeAgent]}
                isReconnecting={connectionStatuses[activeAgent] === 'connecting'}
                onManualReconnect={() => manualReconnect(activeAgent)}
                commands={commands}
                isCommandsLoading={isCommandsLoading}
                handleCommandClick={handleCommandClick}
                onConfirmStay={handleConfirmStay}
                onConfirmLeave={handleConfirmLeave}
                setToastMessage={setToastMessage}
                isAudioEnabled={isAudioEnabled}
                isBotSpeaking={isBotSpeaking}
                isAudioPlayerVisible={isAudioPlayerVisible}
                playBotAudio={playBotAudio}
                stopBotSpeaking={stopBotSpeaking}
                selectedPlusFeatureKey={selectedPlusFeatureKey}
                instructions={instructions}
                isInstructionsLoading={isInstructionsLoading}
                onSelectInstruction={(item) => {
                  const text =
                    typeof item === 'string'
                      ? item
                      : item.function_name || item.question || item.title || item.name;
                  if (text) {
                    setSelectedPlusFeatureKey((prev) => (isAgentFeatureTag(prev) ? prev : null));
                    void handleChatSubmit(text);
                  }
                }}
              />

              {switchNotification && (
                <AgentSwitchNotification
                  notification={switchNotification}
                  onSwitch={handleSwitchAgent}
                  onSkip={handleSkipSwitch}
                />
              )}

            </div>
          </div>



        </div>

        {/* Footer Area - Commands and Input */}
        <div ref={footerRef} className={`embed-box-footer ${isWelcomeOnboarding ? 'embed-box-footer--welcome' : ''}`}>
          <InputMessage
            onSendMessage={(message, images, component) => {
              // Tag thường (Hình ảnh/Video/Audio/Bài viết); TAX/REPORT/GUIDE tự suy từ typeChat
              const tag = selectedPlusFeatureKey
                ? SOCKET_TAG_BY_FEATURE_TAG[selectedPlusFeatureKey]
                : undefined;
              void handleChatSubmit(message, undefined, undefined, images, component, { tag });
            }}
            setToastMessage={setToastMessage}
            handleMicrophoneClick={handleMicrophoneClick}
            stopMicAndDiscard={stopMicAndDiscard}
            pauseMicAndKeepText={pauseMicAndKeepText}
            cancelMicAndClearText={cancelMicAndClearText}
            inputValue={inputValue}
            listening={listening}
            onChangeInput={onChangeInput}
            placeholder={agentConfig?.placeholder ? t(agentConfig.placeholder) : t('common.chatPlaceholder')}
            hasCommands={isCommandsLoading || (commands && commands.length > 0)}
            activeAgent={activeAgent}
            quickFunctions={quickFunctions}
            quickFunctionSearchList={quickFunctionSearchList}
            instructions={instructions}
            selectedPlusFeatureKey={selectedPlusFeatureKey}
            onSelectedPlusFeatureChange={handlePlusFeatureChange}
            onSelectQuickFunction={(item) => handleChatSubmit(item.name, item.id)}
            onSelectInstruction={(item) => {
              const text =
                typeof item === 'string'
                  ? item
                  : item.function_name || item.question || item.title || item.name;
              if (text) {
                setSelectedPlusFeatureKey((prev) => (isAgentFeatureTag(prev) ? prev : null));
                void handleChatSubmit(text);
              }
            }}
            commands={commands}
            isCommandsLoading={isCommandsLoading}
            onSelectCommand={(item) => handleCommandClick(item.name, item.id)}
            quotedMessage={quotedMessage}
            onCancelQuote={handleCancelQuote}
            isChatInputDisabled={isChatInputDisabled}
            isBotSpeaking={isBotSpeaking}
            isThinking={isThinking}
            isMicDisabled={
              toastMessage?.type === 'error' &&
              toastMessage?.text !== 'Bạn chưa có quyền sử dụng micro. Vui lòng bật lại trong browser settings'
            }
            hasVoicePermissionError={Boolean(
              toastMessage?.text === 'Bạn chưa có quyền sử dụng micro. Vui lòng bật lại trong browser settings'
            )}
            isBotStreaming={isBotStreaming}
            onStopStreaming={handleStopStreaming}
            permissions={user?.permissions}
            isOwner={user?.isOwner}
            isComponentPickerDisabled={isComponentPickerDisabled}
          />
        </div>
      </div>
    </div>

      <VoicePermissionModal
        open={Boolean(voiceErrorModalType)}
        type={voiceErrorModalType}
        onClose={closeVoiceErrorModal}
        onRetry={retryVoicePermission}
      />

      <CustomTooltip id="chatbot-tooltip" place="top" />
      <CustomTooltip id="chatbot-menu-tooltip" place="right" positionStrategy="fixed" />
      <CustomTooltip
        id="chatbot-voice-tooltip"
        className="voice-action-tooltip"
        place="top"
        positionStrategy="fixed"
        offset={8}
      />
      <CustomTooltip
        id="cskh-msg-time-tooltip"
        className="cskh-time-tooltip"
        noArrow={true}
        offset={8}
        positionStrategy="fixed"
        middlewares={cskhTimeTooltipMiddlewares}
        delayShow={100}
        delayHide={0}
        closeOnScroll={true}
        closeOnResize={true}
        openEvents={{ mouseenter: true, mouseover: true }}
        closeEvents={{ mouseleave: true, mouseout: true, blur: true }}
        globalCloseEvents={{ scroll: true, resize: true, escape: true, clickOutsideAnchor: true }}
      />


      {/* PDF List Panel - fullscreen overlay */}
      {redirectData && (
        <PdfListOverlay
          data={redirectData}
          onClose={closeRedirectOverlay}
        />
      )}
      <div id="chat-overlay-portal-root" className="embed-box-portal-root" />
    </div>
  );
};

export default EmbedBox;
